import { useAuth } from '@/loaders/Auth'

import type {
	AggregatedDemographics,
	GridVersionPayload,
	ThirdPartyPlatforms,
} from 'honeygrid-types'

/**
 * Generates a list of cells to target based on the draft version filters
 * @param draftVersion
 * @param aggregatedDemographics
 * @returns
 */
export const generateTargetingList = (
	draftVersion: GridVersionPayload | null,
	aggregatedDemographics?: AggregatedDemographics
): string[] => {
	let targetedCells: string[] = []
	if (!draftVersion?.targeting_filters) return targetedCells

	for (const [referenceField, config] of Object.entries(draftVersion.targeting_filters)) {
		if (!aggregatedDemographics) continue
		// use the percentage to determine how many cells to add to the targeting list
		const percentage = config.percentage ?? 0
		const easySelectionIndex = Math.floor(
			(percentage * aggregatedDemographics.sortedCellIds[referenceField].length) / 100
		)
		// get the first n cells from the sorted list
		const filteredCells =
			aggregatedDemographics?.sortedCellIds[referenceField].slice(0, easySelectionIndex) ?? []
		// add to the targeting list
		targetedCells.push(...filteredCells)
	}

	// Handle cell inclusions
	targetedCells = targetedCells.concat(draftVersion?.manual_inclusion_filters ?? [])

	// Handle cell exclusions
	const exclusionSet = new Set(draftVersion?.manual_exclusion_filters ?? [])
	targetedCells = targetedCells.filter((cell) => !exclusionSet.has(cell))

	return [...new Set(targetedCells)]
}

/**
 * Hook that provides targeting functionality with authentication
 */
export const useExportTargeting = () => {
	const { getToken } = useAuth()

	/**
	 * Exports the targeting list to a CSV file
	 * @param cellIds
	 * @param platform
	 * @param workspaceId
	 */
	const exportTargeting = async (
		cellIds: string[],
		platform: ThirdPartyPlatforms,
		workspaceId?: string
	) => {
		try {
			const token = await getToken()

			const response = await fetch(`${import.meta.env.VITE_API_URL}/jwt/api/targeting`, {
				method: 'POST',
				body: JSON.stringify({
					cellIds: cellIds,
					platform,
					workspaceId: workspaceId,
				}),
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
			})

			if (!response.ok) {
				throw new Error('Response not OK')
			}

			const blob = await response.blob()
			const file = window.URL.createObjectURL(blob)
			window.location.assign(file)
		} catch (error) {
			console.error('Failed to export targeting:', error)
			throw new Error('Failed to export targeting')
		}
	}

	return {
		generateTargetingList,
		exportTargeting,
	}
}
